import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import type { Owner, OwnerResidenceInput } from '../api/types';
import { Modal } from '../components/Modal';
import { MutationError } from '../components/MutationError';
import { AddressAutocomplete } from '../places/AddressAutocomplete';
import { AddressCheck } from '../places/AddressCheck';
import { useProject } from '../projects/context';
import { useProjectApi } from '../projects/useProjectApi';
import { countries, propertyLine } from './labels';

type Mode = 'PROPERTY' | 'ADDRESS' | 'UNKNOWN';

// ResidenceDialog: Wohnort des Eigentümers erfassen (OFFENE_FRAGEN B-17) – im Objekt, an einer anderen Anschrift (auch im
// Ausland, mit Adressvorschlägen und Google-Prüfung) oder unbekannt. Deutsche Anschriften prüft der Server streng:
// Straße, Hausnummer, fünfstellige PLZ und Ort, PLZ passend zum Ort.
export function ResidenceDialog({ owner, leadId, onClose }: { owner: Owner; leadId: string; onClose: () => void }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const r = owner.residence;
  const [mode, setMode] = useState<Mode>(r.mode as Mode);
  const [propertyId, setPropertyId] = useState(r.lead_id ?? leadId);
  const a = r.mode === 'ADDRESS' ? r.address : null;
  const [form, setForm] = useState({
    country_code: a?.country_code ?? 'DE',
    street: a?.street ?? '',
    house_number: a?.house_number ?? '',
    address_addition: a?.address_addition ?? '',
    postal_code: a?.postal_code ?? '',
    city: a?.city ?? '',
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: (body: OwnerResidenceInput) => papi.setOwnerResidence(owner.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects', project.id, 'lead-owners'] });
      onClose();
    },
  });
  const de = form.country_code === 'DE';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'PROPERTY') save.mutate({ mode, lead_id: propertyId });
    else if (mode === 'ADDRESS') save.mutate({ mode, address: { ...form } });
    else save.mutate({ mode });
  };
  return (
    <Modal
      title={`Wohnort von ${owner.name}`}
      onClose={onClose}
      size="wide"
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="residence-form" className="btn btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Speichert …' : 'Wohnort speichern'}
          </button>
        </>
      }
    >
      <form id="residence-form" className="residence-form" onSubmit={submit}>
        <fieldset className="residence-modes">
          <legend>Wo wohnt der Eigentümer?</legend>
          <label className="check">
            <input type="radio" name="residence-mode" checked={mode === 'PROPERTY'} onChange={() => setMode('PROPERTY')} />
            Im Objekt
          </label>
          {mode === 'PROPERTY' && owner.properties.length > 1 && (
            <label className="field">
              <span>In welchem Objekt?</span>
              <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
                {owner.properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {propertyLine(p)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="check">
            <input type="radio" name="residence-mode" checked={mode === 'ADDRESS'} onChange={() => setMode('ADDRESS')} />
            An einer anderen Anschrift – auch im Ausland
          </label>
          {mode === 'ADDRESS' && (
            <div className="form-grid residence-address">
              <label className="field">
                <span>Land</span>
                <select value={form.country_code} onChange={set('country_code')}>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <AddressAutocomplete
                label="Straße"
                value={form.street}
                country={form.country_code}
                onChange={(street) => setForm((f) => ({ ...f, street }))}
                context={[form.postal_code, form.city].filter(Boolean).join(' ')}
                onPick={(p) =>
                  setForm((f) => ({
                    ...f,
                    street: p.street || f.street,
                    house_number: p.house_number || f.house_number,
                    postal_code: p.postal_code || f.postal_code,
                    city: p.city || f.city,
                    country_code: p.country_code || f.country_code,
                  }))
                }
              />
              <label className="field narrow">
                <span>Hausnr.</span>
                <input value={form.house_number} onChange={set('house_number')} required={de} maxLength={20} />
              </label>
              <label className="field">
                <span>Adresszusatz</span>
                <input value={form.address_addition} onChange={set('address_addition')} maxLength={120} placeholder="z. B. c/o, Wohnung, Stockwerk" />
              </label>
              <label className="field narrow">
                <span>PLZ</span>
                <input
                  value={form.postal_code}
                  onChange={set('postal_code')}
                  required={de}
                  maxLength={12}
                  inputMode={de ? 'numeric' : undefined}
                  pattern={de ? '[0-9]{5}' : undefined}
                  title={de ? 'Fünf Ziffern' : undefined}
                />
              </label>
              <label className="field">
                <span>Ort</span>
                <input value={form.city} onChange={set('city')} required maxLength={80} />
              </label>
              <AddressCheck
                country={form.country_code}
                address={{ street: form.street, house_number: form.house_number, postal_code: form.postal_code, city: form.city }}
                onApply={(x) => setForm((f) => ({ ...f, ...x }))}
              />
              <p className="muted small residence-hint">
                {de
                  ? 'In Deutschland prüft das System beim Speichern, ob PLZ und Ort zusammenpassen.'
                  : 'Im Ausland sind Straße und Ort Pflicht, die PLZ nur, wenn es eine gibt.'}
              </p>
            </div>
          )}
          <label className="check">
            <input type="radio" name="residence-mode" checked={mode === 'UNKNOWN'} onChange={() => setMode('UNKNOWN')} />
            Wohnt woanders, Anschrift unbekannt
          </label>
        </fieldset>
        {save.isError && <MutationError error={save.error} />}
      </form>
    </Modal>
  );
}
